import { Directive, inject, Input, OnInit } from '@angular/core';
import { Sort, SortDirection } from '@angular/material/sort';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { find } from 'lodash';
import { BehaviorSubject, combineLatest, filter } from 'rxjs';
import { IDataRefreshRequestParams } from '../../../shared/components/datatable/datatable.interface';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { userActions } from '../../../state/user/user.actions';
import { DateUtilitiesService } from '../../../utilities/date-utilities.service';
import { ESnackbar } from '../../../utilities/snackbar.service';
import { BaseCrudPageComponent } from '../../app/base-crud-page.component';
import { IItemsFilterCardFields, TItemView } from '../items-filter-card/items-filter-card.interface';
import { ItemsModalComponent } from '../items-modal/items-modal.component';
import { IBoardTreeStructure, IItemJoins, TUrlItemScope } from '../items.interface';
import { ItemsService } from '../items.service';
import { IItemDatatableState, ItemViewStore } from './item-view.store';
import { ERowClass, IDatatableItem } from './items-kanban/items-kanban.interface';

@Directive({ providers: [ItemViewStore] })
export class BaseItemView
  extends BaseCrudPageComponent<IItemJoins<string>, IItemDatatableState<string>, IItemsFilterCardFields>
  implements OnInit
{
  @Input({ required: true }) public override filterCardData$ =
    new BehaviorSubject<Partial<IItemsFilterCardFields> | null>(null);
  @Input() public boardTreeStructure: IBoardTreeStructure[] = [];
  @Input() public itemScope: TUrlItemScope = null;

  protected store: ItemViewStore;
  protected itemsService = inject(ItemsService);
  protected actionSubject = inject(ActionsSubject);

  protected selectedBoard: IDropdownOption | null = null;
  protected isSelfRefreshTrigger = false;

  constructor(private readonly viewName: TItemView) {
    const store = inject(ItemViewStore);
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfItems',
      multiContext: 'field.actions',
      nameProperty: 'name',
      singleContext: 'field.action',
    });

    this.store = store;
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
        filterCard: this.filterCardData$.pipe(filter(Boolean)),
      }).subscribe(({ datatable, filterCard }) => {
        if (filterCard.itemView !== this.viewName) {
          return;
        }

        this.itemScope = filterCard.itemScope ?? this.itemScope;
        this.selectedBoard = filterCard.board?.length === 1 ? filterCard.board[0] : null;

        this.loadItems(filterCard as IItemsFilterCardFields, datatable);
      }),
      this.itemsService.refreshItems$.subscribe(() => {
        if (this.isSelfRefreshTrigger) {
          this.isSelfRefreshTrigger = false;

          return;
        }

        this.loadItems(this.filterCardData$.value as IItemsFilterCardFields, this.datatableParams$.value!);
      }),
      this.setPageToOneAndRefreshDatatable$.subscribe(() => {
        this.isSelfRefreshTrigger = true;
        this.itemsService.triggerRefresh();
      }),
      this.actionSubject.pipe(ofType(userActions.updateUserConfigurationsCompleted.type)).subscribe(() => {
        this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
      }),
    );
  }

  protected loadItems(filterCard: IItemsFilterCardFields, datatable: IDataRefreshRequestParams): void {
    this.store.loadItems({
      fields: [
        'name',
        'description',
        'key',
        'boardId',
        'boardItemConfigurationId',
        'fieldSetId',
        'currentWorkflowStepId',
        'assigneeId',
        'createdBy',
        'dueDate',
        'createdAt',
        'values',
        'rank',
        'escalatedBoardId',
        'itemRecurrenceId',
      ],
      filters: [...this.itemsService.prepareFilters(filterCard)],
      join: [
        'board||name,key',
        'boardItemConfiguration||name,key,itemType',
        'boardItemConfiguration.itemCategory||name,isDefault,isActive',
        'currentWorkflowStep||name,isDefault,statusCategory',
        'assignee||name',
        'reporter||name',
      ],
      limit: datatable.limit,
      page: datatable.page,
      sort: this.setSort(datatable),
      ...(datatable.search
        ? { search: { searchedFields: ['name', 'key', 'board.name'], searchText: datatable.search ?? '' } }
        : {}),
      additionalCustomSearch: this.itemsService.prepareAdditionalCustomSearch(filterCard, this.boardTreeStructure),
    });
  }

  private setSort(datatable: IDataRefreshRequestParams): Sort[] {
    if (!Array.isArray(datatable.sort)) {
      if (datatable.sort.active === 'id') {
        return [
          {
            active: 'dueDate',
            direction: 'asc' as SortDirection,
          },
          {
            active: 'createdAt',
            direction: 'desc' as SortDirection,
          },
        ];
      }

      return [this.prepareSort(datatable.sort)];
    } else {
      return datatable.sort.map((item) => this.prepareSort(item));
    }
  }

  protected initializeAdd(): void {
    this.setupForAdd();

    this.matDialog.open(ItemsModalComponent, {
      ...ItemsModalComponent.modalParameters,
      data: {
        submitButtonText: this.addEditSubmitButtonText(),
        title: this.addEditModalTitle(),
        ...(this.selectedBoard ? { selectedBoard: this.selectedBoard } : {}),
      },
      restoreFocus: false,
    });
  }

  protected initializeEdit(clickedItemId?: number): void {
    this.setupForEdit();
    const [item] = this.selectedRows();
    const currentItem = clickedItemId ? find(this.store.data$(), { id: clickedItemId })! : item;
    this.itemsService.openItemEditDialogAndSetUrl(
      currentItem.board.key,
      currentItem.id,
      currentItem.key,
      this.addEditSubmitButtonText(),
    );
  }

  protected formatData = (item: IItemJoins<string>): IDatatableItem => {
    return {
      assignee: item.assignee?.name ?? '',
      boardId: item.board.id,
      boardItemConfiguration: item.boardItemConfiguration,
      boardKey: item.board.key,
      boardName: item.board.name,
      createdAt: DateUtilitiesService.convertUTCToUserFormatted(item.createdAt, true),
      currentWorkflowStepId: item.currentWorkflowStepId,
      dueDate: item.dueDate
        ? { formatted: DateUtilitiesService.convertUTCToUserFormatted(item.dueDate, true), original: item.dueDate }
        : { formatted: '', original: '' },
      id: item.id,
      itemCategory: item.boardItemConfiguration.itemCategory?.isDefault
        ? this.translate.instant(`system.label.${item.boardItemConfiguration.itemCategory.name}`)
        : (item.boardItemConfiguration.itemCategory?.name ?? ''),
      itemKey: item.key,
      itemType: this.translate.instant(`business.${item.boardItemConfiguration.itemType}`),
      reporter: item.reporter?.name ?? '',
      rowClass: item.escalatedBoardId ? ERowClass.danger : null,
      status: item.currentWorkflowStep?.isDefault
        ? this.translate.instant(`system.label.${item.currentWorkflowStep.name}`)
        : (item.currentWorkflowStep?.name ?? ''),
      statusCategory: item.currentWorkflowStep?.statusCategory,
      title: item.name,
      titleIcon: item.escalatedBoardId ? { class: 'escalated-board-icon', icon: 'arrow_upward' } : null,
      values: item.values,
    };
  };

  private prepareSort(sort: Sort): Sort {
    switch (sort.active) {
      case 'boardName':
        return { ...sort, active: 'board.name' };
      case 'itemKey':
        return { ...sort, active: 'key' };
      case 'assignee':
        return { ...sort, active: 'assignee.name' };
      case 'reporterName':
        return { ...sort, active: 'reporter.name' };
      case 'title':
        return { ...sort, active: 'name' };
      default:
        return sort;
    }
  }
}
